import React, { useEffect, useState } from 'react'
import { X, ListOrdered, ArrowRight } from 'lucide-react'
import { toast } from 'react-toastify'
import * as Api from '../Services/Api'

// Preview of the old → new numbers, then confirmation. Links and favorites use the
// internal id, so they keep working after the numbers change.
const RenumberModal = ({ isOpen, onClose, onDone }) => {
  const [status, setStatus] = useState('loading') // 'loading' | 'ready' | 'error' | 'saving'
  const [plan, setPlan] = useState(null)

  useEffect(() => {
    if (!isOpen) return
    setStatus('loading')
    setPlan(null)
    Api.fetchRenumberPreview()
      .then((res) => {
        setPlan(res.data)
        setStatus('ready')
      })
      .catch(() => setStatus('error'))
  }, [isOpen])

  if (!isOpen) return null

  const changes = plan?.changes || []

  const confirm = async () => {
    setStatus('saving')
    try {
      const res = await Api.applyRenumber()
      const changed = res.data?.changed ?? changes.length
      toast.success(`${changed} numéro${changed > 1 ? 's' : ''} mis à jour : numérotation de 1 à ${res.data?.total ?? plan.total}`)
      await onDone?.()
      onClose()
    } catch (_err) {
      toast.error('Renumérotation impossible. Réessayez.')
      setStatus('ready')
    }
  }

  return (
    <div
      className="confirmDeleteModal renumberModal"
      onClick={(e) => { if (e.target === e.currentTarget && status !== 'saving') onClose() }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="renumberModal-title"
    >
      <div className="confirmDeleteModal__container">
        <button className="confirmDeleteModal__close" onClick={onClose} aria-label="Fermer" disabled={status === 'saving'}>
          <X size={24} />
        </button>

        <div className="renumberModal__icon"><ListOrdered size={40} /></div>
        <h2 id="renumberModal-title" className="confirmDeleteModal__title">Renuméroter les cantiques</h2>

        {status === 'loading' && <div className="renumberModal__state"><div className="loading-spinner" /></div>}
        {status === 'error' && (
          <p className="confirmDeleteModal__message" role="alert">Impossible de calculer l’aperçu. Vérifiez la connexion puis réessayez.</p>
        )}

        {(status === 'ready' || status === 'saving') && (changes.length === 0 ? (
          <p className="confirmDeleteModal__message">
            La numérotation est déjà continue : <strong>1 à {plan.total}</strong>, sans trou. Rien à changer.
          </p>
        ) : (
          <>
            <p className="confirmDeleteModal__message">
              <strong>{changes.length} numéro{changes.length > 1 ? 's' : ''}</strong> sur {plan.total} vont changer
              pour une numérotation continue de 1 à {plan.total}. L’ordre actuel est conservé.
            </p>
            <ul className="renumberModal__list" aria-label="Aperçu des nouveaux numéros">
              {changes.map((c) => (
                <li key={c.id}>
                  <span className="renumberModal__from">{c.from ?? '—'}</span>
                  <ArrowRight size={13} aria-label="devient" />
                  <span className="renumberModal__to">{c.to}</span>
                  <span className="renumberModal__title">{String(c.title || '').trim()}</span>
                  <small>{c.langue}</small>
                </li>
              ))}
            </ul>
            <p className="confirmDeleteModal__warning">
              Les liens partagés et les favoris restent valides (ils ne dépendent pas du numéro).
            </p>
          </>
        ))}

        <div className="confirmDeleteModal__actions">
          <button className="confirmDeleteModal__btn confirmDeleteModal__btn--cancel" onClick={onClose} disabled={status === 'saving'}>
            {changes.length ? 'Annuler' : 'Fermer'}
          </button>
          {changes.length > 0 && (
            <button
              className="confirmDeleteModal__btn renumberModal__confirm"
              onClick={confirm}
              disabled={status !== 'ready'}
            >
              <ListOrdered size={16} />
              <span>{status === 'saving' ? 'Renumérotation…' : `Renuméroter (${changes.length})`}</span>
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

export default RenumberModal
